import { DecimalPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Observable, timer } from "rxjs";
import { map, take } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandControlBinder } from "~app/common/services/cqs/command-control-binder/command-control-binder";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { DisabledControlDirective } from "../../../../../common/directives/disabled-control/disabled-control.directive";
import { AddLeverantorsorderradCommand } from "../../commands/AddLeverantorsorderradCommand";
import { DeleteLeverantorsorderradCommand } from "../../commands/DeleteLeverantorsorderradCommand";
import { EditLeverantorsorderradCommand } from "../../commands/EditLeverantorsorderradCommand";
import {
    IHandelssortimentsgrupp,
    ILeverantorsorderDetail,
    ILeverantorsorderDetailGrunddata,
    ILeverantorsorderDetailRad,
    IMattslag
} from "../../models/leverantorsorder-detail-model";
import { LeverantorsorderService } from "../../services/leverantorsorder.service";
import { LeverantorsorderradValidation } from "../leverantorsorderrad.validation";

@Component({
    selector: "rask-leverantorsorderrad-edit",
    templateUrl: "./leverantorsorderrad-edit.component.html",
    styleUrls: ["./leverantorsorderrad-edit.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        NgClass,
        DisabledControlDirective,
        InputNumberComponent,
        DatepickerComponent,
        CheckboxComponent,
        InputComponent,
        ButtonComponent,
        DecimalPipe,
        TranslatePipe
    ]
})
export class LeverantorsorderradEditComponent implements OnInit {
    balans = 0;
    @Input() enableCommands = true;
    form: UntypedFormGroup;
    handelssortimentGrupper: Observable<IHandelssortimentsgrupp[]>;
    @Input() isCopy = false;
    isNew = false;
    leverantorsorderGrunddata: ILeverantorsorderDetailGrunddata;
    mattslag: Observable<IMattslag[]>;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("leverantorsorderrad") model: ILeverantorsorderDetailRad;
    newMode = false;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onRemove = new EventEmitter();
    @Input() order: ILeverantorsorderDetail;
    @Input() rootForm: UntypedFormGroup;
    @Input() showLabels = true;
    @Input() title: string;
    @Input() twoRowMode = false;
    updateLevraderAvailable = false;
    validation: IValidationScope;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly commandService = inject(CommandService);
    private readonly formBinder = inject(FormBinderService);
    private readonly leverantorsorderService = inject(LeverantorsorderService);
    private readonly modalService = inject(RaskModalService);
    private readonly translateService = inject(TranslateService);

    constructor() {
        this.leverantorsorderService
            .getLeverantorsorderGrunddata()
            .subscribe(s => this.leverantorsorderGrunddata = s);

        this.initGrunddata();
    }

    bindControlsToCommands() {
        const ctrls = this.form.controls;
        const cmd = this.isNew ? AddLeverantorsorderradCommand : EditLeverantorsorderradCommand;
        const targetId = this.isNew ? this.order.id : this.model.id;
        const commandBinder = new CommandControlBinder(targetId, this.commandService, cmd);

        commandBinder.bindControl(ctrls.handelssortimentsgrupp, (v, c) => c.handelssortimentsgrupp = v?.id);
        commandBinder.bindControl(ctrls.kvantitet, "kvantitet");
        commandBinder.bindControl(ctrls.mattslag, "mattslag");
        commandBinder.bindControl(ctrls.periodTom, "periodTom");
        commandBinder.bindControl(ctrls.periodFrom, "periodFrom");
        commandBinder.bindControl(ctrls.egenBefraktning, "egenBefraktning");
        if (this.isNew) {
            commandBinder.manualPush();
        }
    }

    canEdit = () => this.authenticationService.canEdit();

    delete() {
        const title = this.translateService.instant("LEVERANTORSORDER_DETAIL_DETAIL_ROW_DELETE_PROMPT_HEADER");
        const settings = new ModalSettings( title, this.getDeletePromtMessage());
        settings.contentIsMarkup = true;
        this.modalService.confirm(settings)
            .subscribe(r => {
                if (r === ConfirmDialogResponse.save) {
                    this.commandService.addTransactionCommand(new DeleteLeverantorsorderradCommand(this.model.id, this.order.id));
                    this.onRemove.emit(this.model);
                }
            });
    }

    getClass(size?: number, alternativeSize?: number) {
        const result = [];
        if (size) {
            if (alternativeSize === undefined) {
                alternativeSize = size + 1;
            }
            result.push(`col-${this.twoRowMode ? alternativeSize : size}`);
        }

        if (!this.showLabels) {
            result.push("low");
        }
        return result;
    }

    getDeletePromtMessage() {
        const text = this.translateService.instant("LEVERANTORSORDER_DETAIL_DETAIL_ROW_DELETE_PROMPT_TEXT");
        const lagerplatserMessage = this.model.antalLagertransaktioner > 0
            ? this.translateService.instant("LEVERANTORSORDER_DETAIL_DELETE_CONFIRM_ROW_LAGERTRANSAKTIONER_EXISTS", { antalLagertransaktioner: this.model.antalLagertransaktioner })
            : "";

        return `${text + lagerplatserMessage}`;
    }

    handelssortimentgruppViewFormatter = x => x?.namn;

    initGrunddata() {
        this.handelssortimentGrupper = this.leverantorsorderService.getLeverantorsorderGrunddata().pipe(map((v) => v.handelssortimentsgruppList));
        this.mattslag = this.leverantorsorderService.getLeverantorsorderGrunddata().pipe(map((v) => v.mattslagList));
    }

    initValidation() {
        const validationContext = LeverantorsorderradValidation.init(this.translateService, this.model);

        validationContext.scope(ValidationScopes.skapa).subscribe(v => {
            this.validation = v;
        });
        this.formBinder.attachToRootForm(this.rootForm, this.form, `levorder${this.model.id}`).
            pipe(take(1))
            .subscribe(x => {
                this.formBinder.attachValidation(validationContext.current, this.form, true);
                timer(0).subscribe(() => {
                    this.rootForm.updateValueAndValidity({
                        onlySelf: false,
                        emitEvent: true
                    });
                });
            });
    }

    mattslagFormatter = x => x?.kortnamn;

    ngOnInit(): void {
        this.isNew = this.model.id === 0;
        this.form = this.formBinder.CreateGroup(this.model);
        this.initValidation();
        this.form.valueChanges.subscribe(this.onFormChanges);
        if (this.enableCommands) {
            this.bindControlsToCommands();
        }
        this.updateBalans();
    }

    updateBalans() {
        this.balans = this.leverantorsorderService.getBalans(this.model.levorderLeveransrader, this.model.kvantitet);
    }

    onFormChanges = () => {
        this.updateLevraderAvailable = this.form.controls.periodTom.dirty || this.form.controls.periodFrom.dirty;
        this.updateBalans();
    };

    updateLeveransrader() {
        this.leverantorsorderService.updatePeriodLeverantorsorderrad(this.model.levorderLeveransrader, this.model.periodFrom, this.model.periodTom)
            .subscribe(r => {
                this.model.levorderLeveransrader = r.leveransrader;
                this.updateLevraderAvailable = false;
                this.updateBalans();
            });
    }
}
